// Per-DEVICE UI scale for the POS.
//
// Unlike theme (which follows the user across stations via the DB), zoom is a
// property of the physical screen: a small tablet running the KDS wants the
// whole UI shrunk so more fits without horizontal scroll, while a 24" counter
// monitor read from a few feet back wants it enlarged. So this is
// localStorage-only — never synced to the server, never tied to the user
// record.
//
// Applied by multiplying the ROOT FONT-SIZE via the `--ui-zoom` CSS variable
// (see the `html { font-size: calc(16px * var(--ui-zoom)) }` rule in
// index.css). Every rem-based Tailwind utility scales together, while
// viewport heights (dvh / 100% / vh) keep filling the screen and Radix popover
// positioning stays correct. We deliberately do NOT use CSS `zoom`: it shrank
// the `height:100%` document box and left white space below it on tablets, and
// it mis-placed dropdowns.
//
// ⚠️ ZOOM_DEFAULT and ZOOM_MAX are DIFFERENT CONCEPTS — do not merge them.
// They were the same number (1.0) until the ceiling was raised to 125%, and
// four separate call sites were using ZOOM_MAX to mean "100%, the neutral
// baseline". Every one of them breaks the moment the ceiling moves:
//   - a fallback returning ZOOM_MAX boots every fresh device at the MAXIMUM;
//   - `if (z === ZOOM_MAX) removeProperty(...)` strips the override at the top
//     of the range, so the largest setting silently renders at 100%.
// ZOOM_DEFAULT is "no override, the baseline". ZOOM_MAX is "the ceiling".
// Pinned by lib/ui-zoom.test.ts.

const STORAGE_KEY = 'pos-ui-zoom'

export const ZOOM_MIN = 0.5
/** Neutral 100%: the no-override baseline. NOT the ceiling — see the note above. */
export const ZOOM_DEFAULT = 1.0
export const ZOOM_MAX = 1.25
export const ZOOM_STEP = 0.05

/** Snap to the nearest step and clamp into [ZOOM_MIN, ZOOM_MAX]. */
export function clampZoom(v: number): number {
  if (!Number.isFinite(v)) return ZOOM_DEFAULT
  const snapped = Math.round(v / ZOOM_STEP) * ZOOM_STEP
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(snapped.toFixed(2))))
}

export function readZoom(): number {
  if (typeof window === 'undefined') return ZOOM_DEFAULT
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return ZOOM_DEFAULT
    const n = parseFloat(raw)
    return Number.isFinite(n) ? clampZoom(n) : ZOOM_DEFAULT
  } catch {
    return ZOOM_DEFAULT
  }
}

/**
 * Apply to <html>. At the neutral 100% we remove the override entirely so
 * nothing lingers — note this keys off ZOOM_DEFAULT, not ZOOM_MAX, so the top
 * of the range still writes its variable.
 *
 * Keep in sync with the pre-paint bootstrap in index.html.
 */
export function applyZoom(scale: number): void {
  if (typeof document === 'undefined') return
  const z = clampZoom(scale)
  const style = document.documentElement.style
  if (z === ZOOM_DEFAULT) style.removeProperty('--ui-zoom')
  else style.setProperty('--ui-zoom', String(z))
}

/** Persist (per device) and apply immediately. Returns the clamped value used. */
export function setZoom(scale: number): number {
  const z = clampZoom(scale)
  try {
    window.localStorage.setItem(STORAGE_KEY, String(z))
  } catch {
    /* ignore — Safari private mode */
  }
  applyZoom(z)
  return z
}
